import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from 'src/auth/useAuthContext';
import { LOCAL_STORAGE_KEYS } from 'src/constants/localStorage';
import { SearchBookAddressFormValuesProps } from '../components/SearchBook/SearchBookAddress';
import { SearchBookProfileFormValuesProps } from '../components/SearchBook/SearchBookProfile';
import { SearchBookUserFormValuesProps } from '../components/SearchBook/SearchBookUser';
import { SearchBookProfessionalValuesProps } from '../components/SearchResultsDetails/SearchResultsOrganizationProfessionalsListCard/SearchResultsOrganizationProfessionalsListCardTabCalendar';
// ----------------------------------------------------------------------

export type SearchBookStepFormValues = [
  SearchBookProfessionalValuesProps | undefined,
  SearchBookUserFormValuesProps | undefined,
  (
    | SearchBookAddressFormValuesProps
    | SearchBookProfileFormValuesProps
    | undefined
  ),
  SearchBookProfileFormValuesProps | undefined,
];

interface SearchBookContextType {
  activeStep: number;
  setActiveStep: (step: number) => void;
  completedSteps: boolean[];
  handleNext: (values: any) => void;
  handleBack: (values?: any) => void;
  stepsFormValues: SearchBookStepFormValues;
  handleStepFormValues: (values: any, step: number) => void;
  resetToDefault: () => void;
  handleDateSelection: (values: any) => void;
  selectedDate: Date | undefined;
  handleStepClick: (step: number) => void;
  handleCalendarNext: () => void;
  isAuthenticated: boolean;
  isBookingInProgress: boolean;
  setIsBookingInProgress: (value: boolean) => void;
  isMobileVisit: boolean;
  summaryStep: number;
  profileStepIndex: number;
}

const SearchBookContext = createContext<SearchBookContextType | undefined>(
  undefined,
);

interface SearchBookProviderProps {
  children: React.ReactNode;
}

export function SearchBookProvider({ children }: SearchBookProviderProps) {
  const { isAuthenticated } = useAuthContext();
  const location = useLocation();

  const loadStoredFormValues = () => {
    const storedValues = localStorage.getItem(
      LOCAL_STORAGE_KEYS.BOOKING_STEPS_FORM_VALUES,
    );
    if (storedValues) {
      return JSON.parse(storedValues);
    }
    return [undefined, undefined, undefined, undefined];
  };

  const loadStoredActiveStep = (isAuthenticated: boolean) => {
    const storedStep = localStorage.getItem(
      LOCAL_STORAGE_KEYS.BOOKING_ACTIVE_STEP,
    );
    if (storedStep) {
      const parsedStep = parseInt(storedStep, 10);
      // Validate that the parsed step is a valid number and within range (1-4)
      const maxStep = 4; // Max possible step (mobile visit with address step)
      if (!isNaN(parsedStep) && parsedStep >= 1 && parsedStep <= maxStep) {
        // Additional validation based on authentication state
        if (isAuthenticated) {
          // For authenticated users: step 1 (user info) is skipped
          if (parsedStep >= 2) {
            return parsedStep;
          }
        } else {
          return parsedStep;
        }
      }
    }
    // If no stored step or invalid step, return initial step
    return getInitialActiveStep(isAuthenticated);
  };

  const getInitialActiveStep = (isAuthenticated: boolean) => {
    // If authenticated, skip the user info step (step 1) and start at profile step (step 2)
    // For non-authenticated users, start at step 1 (user form)
    return isAuthenticated ? 2 : 1;
  };

  const calculateCompletedSteps = (
    currentActiveStep: number,
    isAuthenticated: boolean,
    isMobile: boolean = false,
  ) => {
    const totalSteps = isMobile ? 4 : 3;
    const completed = new Array(totalSteps).fill(false);
    // Mark all steps BEFORE the current active step as completed (not including current)
    for (let i = 0; i < currentActiveStep - 1; i++) {
      completed[i] = true;
    }
    // For authenticated users, mark step 0 (user info - index 0) as completed since it's skipped
    if (isAuthenticated) {
      completed[0] = true;
    }
    return completed;
  };

  const [stepsFormValues, setStepsFormValues] =
    useState<SearchBookStepFormValues>(loadStoredFormValues());

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [isBookingInProgress, setIsBookingInProgress] = useState(false);

  const isMobileVisit = stepsFormValues[0]?.selectedVisitType === 'mobile';

  const summaryStep = isMobileVisit ? 4 : 3;

  const profileStepIndex = isMobileVisit ? 3 : 2;

  const [activeStep, setActiveStep] = useState(() =>
    loadStoredActiveStep(isAuthenticated),
  );
  const [completedSteps, setCompletedSteps] = useState<boolean[]>(() => {
    const storedStep = loadStoredActiveStep(isAuthenticated);
    return calculateCompletedSteps(storedStep, isAuthenticated);
  });

  // Only reset on initial navigation, not when auth state changes mid-booking
  useEffect(() => {
    if (location.pathname.includes('/booking')) {
      // Don't reset if we're in the middle of a booking process or if we have valid stored data
      const storedStep = localStorage.getItem(
        LOCAL_STORAGE_KEYS.BOOKING_ACTIVE_STEP,
      );
      const hasValidStoredStep = storedStep && !isNaN(parseInt(storedStep, 10));

      if (!isBookingInProgress && !hasValidStoredStep) {
        // No valid stored step, initialize to the beginning
        const newActiveStep = getInitialActiveStep(
          isAuthenticated,
        );
        setActiveStep(newActiveStep);
        setCompletedSteps(
          calculateCompletedSteps(
            newActiveStep,
            isAuthenticated,
          ),
        );
      }
    }
  }, [location.pathname, isAuthenticated, isBookingInProgress]);

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.BOOKING_STEPS_FORM_VALUES,
      JSON.stringify(stepsFormValues),
    );
  }, [stepsFormValues]);

  // Persist activeStep to localStorage
  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.BOOKING_ACTIVE_STEP,
      activeStep.toString(),
    );
  }, [activeStep]);

  // Handle authentication state changes during booking
  useEffect(() => {
    // If user logs in while on step 1 (user info), skip to step 2 (profile)
    if (isAuthenticated && activeStep === 1) {
      setActiveStep(2);
      setCompletedSteps((prev) => {
        const newCompleted = [...prev];
        newCompleted[0] = true; // Mark step 0 (user info) as completed
        return newCompleted;
      });
    }
    // If user logs out while on step 2 or beyond, and step 1 is not completed, go back to step 1
    if (!isAuthenticated && activeStep >= 2) {
      const hasUserData = !!stepsFormValues[1];
      if (!hasUserData) {
        setActiveStep(1);
        const totalSteps = isMobileVisit ? 4 : 3;
        setCompletedSteps(new Array(totalSteps).fill(false));
      }
    }
  }, [isAuthenticated, activeStep, stepsFormValues]);

  const handleNext = useCallback(
    (stepFormValues: any) => {
      setStepsFormValues((prevSteps) => {
        const newSteps: SearchBookStepFormValues = [...prevSteps];
        newSteps[activeStep] = stepFormValues;
        return newSteps;
      });

      setCompletedSteps((prevCompletedSteps) => {
        const newCompletedSteps = [...prevCompletedSteps];
        // Mark the current step as completed (activeStep is 1-based, array is 0-based)
        if (activeStep === 1) {
          newCompletedSteps[0] = true; // Opiekun completed
        } else if (activeStep === 2) {
          if (isMobileVisit) {
            newCompletedSteps[1] = true; // Address completed
          } else {
            newCompletedSteps[1] = true; // Pupil completed
            newCompletedSteps[2] = true; // Also mark Summary as completed
          }
        } else if (isMobileVisit && activeStep === 3) {
          newCompletedSteps[2] = true; // Pupil completed
          newCompletedSteps[3] = true; // Also mark Summary as completed
        }

        return newCompletedSteps;
      });

      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    },
    [activeStep, isMobileVisit],
  );

  const handleDateSelection = useCallback(
    (stepFormValues: any) => {
      setStepsFormValues((prevSteps) => {
        const newSteps: SearchBookStepFormValues = [...prevSteps];
        newSteps[activeStep] = stepFormValues;
        return newSteps;
      });
    },
    [activeStep],
  );

  const handleCalendarNext = useCallback(() => {
    setCompletedSteps((prevCompletedSteps) => {
      const newCompletedSteps = [...prevCompletedSteps];
      newCompletedSteps[activeStep] = true;
      if (activeStep === 2) {
        newCompletedSteps[2] = true;
      }

      return newCompletedSteps;
    });

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }, [activeStep]);

  const handleBack = useCallback(
    (stepFormValues?: any) => {
      setStepsFormValues((prevSteps) => {
        const newSteps: SearchBookStepFormValues = [...prevSteps];
        newSteps[activeStep] = stepFormValues;
        return newSteps;
      });

      const newActiveStep = activeStep - 1;
      setActiveStep(newActiveStep);

      // Recalculate completed steps based on the new active step
      setCompletedSteps(
        calculateCompletedSteps(
          newActiveStep,
          isAuthenticated,
          isMobileVisit,
        ),
      );
    },
    [activeStep, isAuthenticated, isMobileVisit],
  );

  const handleStepClick = useCallback(
    (stepIndex: number) => {
      // Allow clicking on completed steps or the current step
      // For the current step, stepIndex + 1 === activeStep
      if (completedSteps[stepIndex] || stepIndex + 1 === activeStep) {
        setActiveStep(stepIndex + 1);
      }
    },
    [completedSteps, activeStep],
  );

  const resetToDefault = useCallback(() => {
    // Reset all state to default values
    setCompletedSteps([false, false, false]);
    setStepsFormValues([undefined, undefined, undefined, undefined]);

    // Clear localStorage
    localStorage.removeItem(LOCAL_STORAGE_KEYS.BOOKING_STEPS_FORM_VALUES);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.BOOKING_ACTIVE_STEP);
  }, []);

  const handleStepFormValues = useCallback((values: any, step: number) => {
    setStepsFormValues((prevSteps) => {
      const newSteps: SearchBookStepFormValues = [...prevSteps];
      newSteps[step] = values;
      return newSteps;
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      activeStep,
      setActiveStep,
      completedSteps,
      handleNext,
      handleBack,
      stepsFormValues,
      handleStepFormValues,
      resetToDefault,
      handleDateSelection,
      selectedDate,
      handleStepClick,
      handleCalendarNext,
      isAuthenticated: isAuthenticated,
      isBookingInProgress,
      setIsBookingInProgress,
      isMobileVisit,
      summaryStep,
      profileStepIndex,
    }),
    [
      activeStep,
      completedSteps,
      handleBack,
      handleNext,
      handleStepClick,
      handleStepFormValues,
      handleCalendarNext,
      handleDateSelection,
      selectedDate,
      resetToDefault,
      stepsFormValues,
      isAuthenticated,
      isBookingInProgress,
      isMobileVisit,
      summaryStep,
      profileStepIndex,
    ],
  );

  return (
    <SearchBookContext.Provider value={contextValue}>
      {children}
    </SearchBookContext.Provider>
  );
}

export { SearchBookContext };
export type { SearchBookContextType };
